import { forwardRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Sparkle } from "lucide-react";
import type { Chapter } from "../types";
import { yomaFor } from "../delight";

/**
 * A chapter unlock — the level-up moment between rhythms. Auto-dismisses
 * after three seconds; tap, Space or Enter moves on early.
 *
 * Presentation only: it does not gate, skip or reorder anything.
 */
export const ChapterCard = forwardRef<HTMLDivElement, { chapter: Chapter; onDone: () => void }>(
  function ChapterCard({ chapter, onDone }, ref) {
  const yoma = yomaFor(chapter.id);
  const reduce = useReducedMotion();
  return (
    <motion.div
      ref={ref}
      key={chapter.id}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-40 grid place-items-center bg-background/95 backdrop-blur-md px-6"
      onClick={onDone}
      role="button"
      tabIndex={0}
      aria-label={`Chapter unlocked: ${chapter.title}. Continue`}
      onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") onDone(); }}
    >
      {/* A quiet celebration: a few sparks, no confetti cannon. Skipped
          entirely when the learner asks for reduced motion. */}
      {!reduce && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
          {SPARKS.map((s, i) => (
            <motion.span
              key={i}
              className="absolute text-primary/70"
              style={{ left: s.left, top: s.top }}
              initial={{ opacity: 0, scale: 0.4, y: 8 }}
              animate={{ opacity: [0, 1, 0], scale: [0.4, 1, 0.7], y: -14 }}
              transition={{ duration: 1.8, delay: i * 0.12, repeat: Infinity, repeatDelay: 0.6 }}
            >
              <Sparkle className="h-4 w-4" />
            </motion.span>
          ))}
        </div>
      )}


      <motion.div
        initial={{ y: 12, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-lg text-center"
      >
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-4">
          Chapter unlocked
        </div>
        <h2 className="font-display text-4xl md:text-5xl font-medium text-foreground">
          {chapter.title}
        </h2>
        <p className="mt-4 text-muted-foreground text-lg">{chapter.blurb}</p>
        {yoma && (
          <p className="mt-6 text-sm text-foreground/80">
            <span className="font-medium">YoMA:</span> {yoma.intro}
          </p>
        )}
        <p className="mt-8 text-xs text-muted-foreground/60">Tap anywhere to continue</p>
      </motion.div>
    </motion.div>
  );
});

const SPARKS = [
  { left: "12%", top: "22%" },
  { left: "82%", top: "30%" },
  { left: "24%", top: "72%" },
  { left: "70%", top: "68%" },
  { left: "48%", top: "16%" },
];
